import React from "react";
import { Box } from "app/layout/Box/Box";
import { ColumnContainer, ColumnContainerLayouts } from "app/layout/ColumnContainer/ColumnContainer";
import { PerformanceLabel } from "app/performance/PerformanceLabel/PerformanceLabel";
import { BasicChart, ChartData } from "app/charts/BasicChart/BasicChart";
import { AttributeList } from "app/layout/AttributeList/AttributeList";

import "./style.scss";
import { chartPeriods } from "app/charts/types";
import { PeriodSelector } from "app/charts/PeriodSelector/PeriodSelector";
import { useContextualMoneyFormatter } from "app/formatting/hooks";
import { LoadingIndicator } from "app/layout/LoadingIndicator/LoadingIndicator";

export interface StageProps {
    headValue?: number | null;
    headCurrency?: string | null;
    headPerformance?: number | null;
    headInfoItems: string[];
    chartData: ChartData;
    loading?: boolean;
    chartError?: any;
    /** Shown right next to the chart */
    children?: React.ReactNode;
    selectedPeriod?: chartPeriods;
    onPeriodChange?: React.Dispatch<chartPeriods>;
    /** Should the option "Intraday" be removed from the chart period selector? */
    noIntraday?: boolean;
    lastExchangeClose?: string;
    lastExchangeCloseValue?: number;
    todayExchangeOpen?: string | null;
}

export const Stage = ({
    headValue,
    headCurrency,
    headPerformance,
    headInfoItems,
    chartData,
    loading,
    chartError,
    children = null,
    selectedPeriod = chartPeriods.ONE_YEAR,
    onPeriodChange,
    lastExchangeClose,
    lastExchangeCloseValue,
    todayExchangeOpen,
    noIntraday
}: StageProps) => {
    const formatMoney = useContextualMoneyFormatter();

    return (
        <Box section alternateBackground className="stage">
            <ColumnContainer
                spacing={24}
                layout={children ? ColumnContainerLayouts.TWO_ONE : ColumnContainerLayouts.AUTO}
            >
                <section className={loading ? "loading" : ""}>
                    <header>
                        <b>{formatMoney(headValue, headCurrency)}</b>
                        <PerformanceLabel value={headPerformance} noBackground />
                    </header>
                    <AttributeList items={headInfoItems} />
                    <BasicChart
                        value={chartData}
                        period={selectedPeriod}
                        error={chartError || (!loading && (!chartData || !chartData.length))}
                        todayExchangeOpen={todayExchangeOpen}
                        lastExchangeClose={lastExchangeClose}
                        lastExchangeCloseValue={lastExchangeCloseValue}
                    />
                    <PeriodSelector value={selectedPeriod} onChange={onPeriodChange} noIntraday={noIntraday} />
                    <LoadingIndicator visible={!!loading} />
                </section>
                {children}
            </ColumnContainer>
        </Box>
    );
};
